import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({ total_enquiries: 0, today_enquiries: 0 });
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [enquiriesRes, statsRes, pricingRes] = await Promise.all([
        axios.get(`${API}/enquiry/list`),
        axios.get(`${API}/enquiry/stats`),
        axios.get(`${API}/enquiry/pricing`)
      ]);
      
      setEnquiries(enquiriesRes.data);
      setStats(statsRes.data);
      setPricing(pricingRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl font-semibold text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Panel</h1>
            <p className="text-gray-600 mt-1">Alee Club Enquiry Management</p>
          </div>
          <Button onClick={fetchData} className="bg-red-600 hover:bg-red-700">
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Total Enquiries</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats.total_enquiries}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Today's Enquiries</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">{stats.today_enquiries}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Current Price</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">₹{pricing?.current_price}</div>
              {pricing?.is_early_bird && (
                <Badge className="mt-2 bg-yellow-500">Early Bird Active</Badge>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Days Remaining</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {pricing?.days_remaining !== null ? pricing?.days_remaining : 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {pricing?.is_early_bird ? 'Early bird ends soon' : 'Regular pricing'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Enquiries Table */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Enquiries</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Date & Time</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Name</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Age</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Phone</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Email</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">City</th>
                  </tr>
                </thead>
                <tbody>
                  {enquiries.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-8 text-gray-500">
                        No enquiries yet
                      </td>
                    </tr>
                  ) : (
                    enquiries.map((enquiry) => (
                      <tr key={enquiry.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-600">{formatDate(enquiry.created_at)}</td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900">{enquiry.name}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{enquiry.age}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{enquiry.phone}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{enquiry.email}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{enquiry.city}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPanel;